import React, { useState } from "react";
import { api, formatMoney, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Wallet, Plus, Trash2, BanknoteArrowUp, Smartphone, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";
import EstadoPagoBadge from "@/components/EstadoPagoBadge";

const METODO_INFO = {
  efectivo: { label: "Efectivo", Icon: BanknoteArrowUp },
  mercado_pago: { label: "Mercado Pago", Icon: Smartphone },
  transferencia: { label: "Transferencia", Icon: ArrowLeftRight },
};

export default function PagosPanel({ pedido, onChange }) {
  const [metodo, setMetodo] = useState("efectivo");
  const [monto, setMonto] = useState("");
  const [nota, setNota] = useState("");
  const [busy, setBusy] = useState(false);

  const saldo = Number(pedido.saldo_pendiente ?? 0);
  const pagado = Number(pedido.monto_pagado ?? 0);
  const total = Number(pedido.total ?? 0);
  const cuentaCorriente = !!pedido.cuenta_corriente;
  const tieneClienteId = !!pedido.cliente_id;

  const registrar = async () => {
    const m = Number(monto);
    if (!m || m <= 0) {
      toast.error("Ingresá un monto válido");
      return;
    }
    if (m > saldo + 0.005) {
      toast.error(`El monto excede el saldo (${formatMoney(saldo)})`);
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(`/pedidos/${pedido.id}/pagos`, {
        metodo,
        monto: m,
        nota: nota.trim(),
      });
      toast.success("Pago registrado");
      setMonto("");
      setNota("");
      onChange?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al registrar pago");
    } finally {
      setBusy(false);
    }
  };

  const eliminarPago = async (pagoId) => {
    setBusy(true);
    try {
      const res = await api.delete(`/pedidos/${pedido.id}/pagos/${pagoId}`);
      toast.success("Pago eliminado");
      onChange?.(res.data);
    } catch {
      toast.error("Error al eliminar el pago");
    } finally {
      setBusy(false);
    }
  };

  const toggleCuentaCorriente = async (val) => {
    if (!tieneClienteId) {
      toast.error("Este pedido no tiene un cliente registrado");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(`/pedidos/${pedido.id}/cuenta-corriente`, {
        cuenta_corriente: val,
      });
      toast.success(val ? "Marcado a cuenta corriente" : "Desmarcado de cuenta corriente");
      onChange?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al actualizar");
    } finally {
      setBusy(false);
    }
  };

  const fillSaldo = () => setMonto(saldo > 0 ? String(saldo) : "");

  return (
    <Card className="p-5 sm:p-6 border-[#E5E0D8] rounded-2xl bg-white" data-testid="pagos-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Wallet className="h-4 w-4 text-[#C85A32]" />
          <h2 className="font-display font-bold text-xl text-[#1A1A1A]">Pagos</h2>
        </div>
        <EstadoPagoBadge estado={pedido.estado_pago} />
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-2 mb-5">
        <div className="rounded-lg bg-[#F9F6F0] p-3">
          <div className="text-[10px] uppercase tracking-wider text-[#8C8C8C]">Total</div>
          <div className="font-mono font-bold text-[15px] text-[#1A1A1A]">{formatMoney(total)}</div>
        </div>
        <div className="rounded-lg bg-[#DAE6D7] p-3">
          <div className="text-[10px] uppercase tracking-wider text-[#3F6B3C]">Pagado</div>
          <div className="font-mono font-bold text-[15px] text-[#3F6B3C]">{formatMoney(pagado)}</div>
        </div>
        <div className={`rounded-lg p-3 ${saldo > 0 ? "bg-[#FDF3E1]" : "bg-[#F0F0F0]"}`}>
          <div className="text-[10px] uppercase tracking-wider text-[#8C8C8C]">Saldo</div>
          <div className={`font-mono font-bold text-[15px] ${saldo > 0 ? "text-[#A6751F]" : "text-[#595959]"}`}>
            {formatMoney(saldo)}
          </div>
        </div>
      </div>

      {/* Cuenta corriente toggle */}
      {tieneClienteId && (
        <div className="flex items-center justify-between rounded-lg border border-[#E5E0D8] px-3 py-2.5 bg-[#FBF8F2] mb-5">
          <div>
            <div className="text-sm font-semibold text-[#1A1A1A]">Cuenta corriente</div>
            <div className="text-[11px] text-[#8C8C8C]">
              Marca este pedido como fiado. El saldo suma a la deuda del cliente.
            </div>
          </div>
          <Switch
            checked={cuentaCorriente}
            onCheckedChange={toggleCuentaCorriente}
            disabled={busy}
            data-testid="pago-toggle-cc"
          />
        </div>
      )}

      {/* Registrar pago */}
      {saldo > 0 && (
        <div className="rounded-xl border border-[#E5E0D8] p-3 sm:p-4 bg-white mb-5">
          <div className="text-[11px] uppercase tracking-wider text-[#8C8C8C] font-semibold mb-2.5">
            Registrar pago
          </div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div>
              <Label className="text-[10px] text-[#8C8C8C]">Método</Label>
              <Select value={metodo} onValueChange={setMetodo}>
                <SelectTrigger className="h-10 bg-white" data-testid="pago-metodo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="mercado_pago">Mercado Pago</SelectItem>
                  <SelectItem value="transferencia">Transferencia</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] text-[#8C8C8C]">Monto</Label>
              <div className="flex gap-1">
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={monto}
                  onChange={(e) => setMonto(e.target.value)}
                  placeholder="0"
                  className="h-10 font-mono"
                  data-testid="pago-monto"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 px-2 text-[11px] border-[#E5E0D8]"
                  onClick={fillSaldo}
                  data-testid="pago-fill-saldo"
                  title="Usar saldo pendiente"
                >
                  Saldo
                </Button>
              </div>
            </div>
          </div>
          <div className="mb-3">
            <Label className="text-[10px] text-[#8C8C8C]">Nota (opcional)</Label>
            <Input
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              placeholder="Ej: cobrado por Dani"
              className="h-10 bg-white"
              data-testid="pago-nota"
            />
          </div>
          <Button
            type="button"
            className="w-full bg-[#C85A32] hover:bg-[#A64A29] text-white h-10 gap-2"
            onClick={registrar}
            disabled={busy}
            data-testid="pago-registrar"
          >
            <Plus className="h-4 w-4" /> Registrar pago
          </Button>
        </div>
      )}

      {/* Lista de pagos */}
      <div>
        <div className="text-[11px] uppercase tracking-wider text-[#8C8C8C] font-semibold mb-2">
          Historial
        </div>
        {(!pedido.pagos || pedido.pagos.length === 0) ? (
          <div className="text-sm text-[#8C8C8C] italic py-3">Sin pagos registrados.</div>
        ) : (
          <ul className="divide-y divide-[#F0EBE0]" data-testid="pagos-list">
            {pedido.pagos.map((p) => {
              const meta = METODO_INFO[p.metodo] || { label: p.metodo, Icon: Wallet };
              const Icon = meta.Icon;
              return (
                <li key={p.id} className="py-2.5 flex items-center justify-between gap-2" data-testid={`pago-row-${p.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="h-8 w-8 rounded-lg bg-[#F4EAE6] text-[#C85A32] flex items-center justify-center shrink-0">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[#1A1A1A]">{meta.label}</div>
                      <div className="text-[11px] text-[#8C8C8C]">
                        {formatDate(p.fecha)}
                        {p.nota && <> · {p.nota}</>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="font-mono font-bold text-[#1A1A1A]">{formatMoney(p.monto)}</div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          type="button"
                          className="h-8 w-8 rounded-md text-[#8C8C8C] hover:bg-[#FBEBEB] hover:text-[#8E2E2E] flex items-center justify-center"
                          aria-label="Eliminar pago"
                          data-testid={`pago-del-${p.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Eliminar pago</AlertDialogTitle>
                          <AlertDialogDescription>
                            Se eliminará el pago de {formatMoney(p.monto)} ({meta.label}).
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Volver</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => eliminarPago(p.id)}
                            className="bg-[#B23A3A] hover:bg-[#8E2E2E]"
                          >
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Card>
  );
}
